<template>
    <div class="valuation-form">
        <form
            id="valuation-form-element"
            method="post"
            action="/valuation"
            novalidate
        >
            <input
                type="hidden"
                name="_token"
                :value="csrf"
            >

            <div class="valuation-form-header">
                <span
                    class="section-title"
                    :class="formStage === 'property-location' ? 'active' : ''"
                >
                    Property Location
                </span>
                <span
                    class="section-title"
                    :class="formStage === 'property-details' ? 'active' : ''"
                >
                    Property Details
                </span>
                <span
                    class="section-title"
                    :class="formStage === 'contact-details' ? 'active' : ''"
                >
                    Contact Details
                </span>
                <div class="section-progress-bar" />
            </div>

            <div
                v-show="formStage === 'property-location'"
                class="valuation-form-section valuation-form-section--property-location"
            >
                <div
                    v-show="mapAddressInput === true"
                    class="map-address-input"
                >
                    <div
                        id="google-map"
                        :style="{ backgroundImage: 'url(' + mapsImgSrc + ')' }"
                    />

                    <input
                        id="pac-input"
                        type="text"
                        placeholder="Enter your street address"
                        autocomplete="off"
                        required
                        @input="validate($event.target)"
                        @blur="validate($event.target)"
                    >
                </div>

                <div
                    v-show="mapAddressInput === false"
                    class="manual-address-input"
                >
                    <div class="manual-address-street">
                        <label class="form-input-label-element">
                            <span class="form-input-label">Street Address</span>
                            <input
                                id="manual-address-street-input"
                                class="valuation-form-input-box"
                                name="selling_address"
                                type="text"
                                required
                                @input="validate($event.target)"
                                @blur="validate($event.target)"
                            >
                            <span class="form-input-error">Please complete this required field</span>
                        </label>
                    </div>

                    <div class="manual-address-suburb">
                        <label class="form-input-label-element">
                            <span class="form-input-label">Suburb</span>
                            <input
                                id="manual-address-suburb-input"
                                class="valuation-form-input-box"
                                name="suburb"
                                type="text"
                                required
                                @input="validate($event.target)"
                                @blur="validate($event.target)"
                            >
                            <span class="form-input-error">Please complete this required field</span>
                        </label>
                    </div>

                    <input
                        id="hidden-input-lat"
                        name="property_address_latitude"
                        type="hidden"
                    >

                    <input
                        id="hidden-input-lng"
                        name="property_address_longitude"
                        type="hidden"
                    >
                </div>

                <div class="valuation-form-section-footer">
                    <button
                        class="valuation-form-section-footer--map-manual"
                        :class="mapAddressInput ? '' : 'manual-input'"
                        type="button"
                        @click="mapAddressInput = !mapAddressInput"
                    >
                        {{ mapAddressInput ? 'Or enter your address manually' : 'Or locate your property on map' }}
                    </button>
                    <button
                        class="valuation-form-section-footer--next"
                        type="button"
                        @click="formStage = validatePropertyLocation() ? 'property-details' : formStage"
                    >
                        Next
                    </button>
                </div>
            </div>

            <div
                v-show="formStage === 'property-details'"
                class="valuation-form-section valuation-form-section--property-details"
            >
                <div class="property-details-input">
                    <label class="form-input-label-element">
                        <span class="form-input-label">Valuation Property Type</span>
                        <div class="valuation-form-select-box-arrow" />
                        <select
                            id="property-details-type-select"
                            class="valuation-form-select-box"
                            name="property_type"
                            required
                            @input="validate($event.target)"
                            @blur="validate($event.target)"
                            @click="$event.target.focus()"
                        >
                            <option value="">Please select</option>
                            <option value="House">House</option>
                            <option value="Apartment">Apartment</option>
                            <option value="Vacant Land">Vacant Land</option>
                            <option value="Townhouse">Townhouse</option>
                            <option value="Industrial">Industrial</option>
                            <option value="Retail">Retail</option>
                            <option value="Offices">Offices</option>
                            <option value="Farm">Farm</option>
                            <option value="Hospitality">Hospitality</option>
                        </select>
                        <span class="form-input-error">Please select an option from the dropdown menu</span>
                    </label>
                    <label class="form-input-label-element">
                        <span class="form-input-label">Building or Estate Name (If applicable)</span>
                        <input
                            class="valuation-form-input-box"
                            name="property_address_complex_estate_name"
                            type="text"
                        >
                    </label>
                </div>

                <div class="valuation-form-section-footer">
                    <button
                        class="valuation-form-section-footer--prev"
                        type="button"
                        @click="formStage = 'property-location'"
                    >
                        Go back
                    </button>
                    <button
                        class="valuation-form-section-footer--next"
                        type="button"
                        @click="formStage = validatePropertyDetails() ? 'contact-details' : formStage"
                    >
                        Next
                    </button>
                </div>
            </div>

            <div
                v-show="formStage === 'contact-details'"
                class="valuation-form-section valuation-form-section--contact-details"
            >
                <div class="contact-details-input">
                    <input
                        id="hidden-input-fullname"
                        name="name"
                        type="hidden"
                    >

                    <div class="form-input-gridbox">
                        <label class="form-input-label-element">
                            <span class="form-input-label">First name</span>
                            <input
                                id="contact-details-firstname-input"
                                class="valuation-form-input-box"
                                name="firstname"
                                type="text"
                                required
                                @input="validate($event.target)"
                                @blur="validate($event.target)"
                            >
                            <span class="form-input-error">Please complete this required field</span>
                        </label>
                    </div>

                    <div class="form-input-gridbox">
                        <label class="form-input-label-element">
                            <span class="form-input-label">Last name</span>
                            <input
                                id="contact-details-lastname-input"
                                class="valuation-form-input-box"
                                name="lastname"
                                type="text"
                                required
                                @input="validate($event.target)"
                                @blur="validate($event.target)"
                            >
                            <span class="form-input-error">Please complete this required field</span>
                        </label>
                    </div>

                    <div class="form-input-gridbox">
                        <label class="form-input-label-element">
                            <span class="form-input-label">Email address</span>
                            <input
                                id="contact-details-email-input"
                                class="valuation-form-input-box"
                                name="email"
                                type="email"
                                required
                                @input="validate($event.target)"
                                @blur="validate($event.target)"
                            >
                            <span class="form-input-error">Please input a valid email address</span>
                        </label>
                    </div>

                    <div class="form-input-gridbox">
                        <label class="form-input-label-element">
                            <span class="form-input-label">Phone number</span>
                            <input
                                id="contact-details-phone-input"
                                class="valuation-form-input-box"
                                name="number"
                                type="tel"
                                pattern="(?:[\s+\-()x.]*[0-9][\s+\-()x.]*){7,}"
                                required
                                @input="validate($event.target)"
                                @blur="validate($event.target)"
                            >
                            <span class="form-input-error">Please enter a valid phone number</span>
                        </label>
                    </div>

                    <div class="form-input-gridbox">
                        <label class="form-input-label-element">
                            <input
                                id="contact-details-subscribe-input"
                                class="valuation-form-checkbox"
                                name="subscribe"
                                type="checkbox"
                                value="1"
                            >
                            <span class="form-input-label">Send me useful tips on the property market</span>
                        </label>
                    </div>
                </div>

                <div class="valuation-form-section-footer">
                    <button
                        class="valuation-form-section-footer--prev"
                        type="button"
                        @click="formStage = 'property-details'"
                    >
                        Go back
                    </button>
                    <button
                        class="valuation-form-section-footer--submit"
                        type="button"
                        @click="formSubmit()"
                    >
                        Submit
                    </button>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
export default {
    props: {
        startUrl: {
            type: String,
            required: true,
        },
        csrf: {
            type: String,
            required: true,
        },
    },
    data: function () {
        return {
            imgUrl: null,
            mapAddressInput: true,
            formStage: 'property-location',
        };
    },
    computed: {
        mapsImgSrc: function () {
            return this.imgUrl || this.startUrl;
        },
    },
    mounted () {
        this.initMap();
    },
    methods: {
        initMap () {
            let input = document.getElementById('pac-input');
            let autocomplete = new google.maps.places.Autocomplete(input);
            autocomplete.setFields([ 'address_components', 'geometry', 'icon', 'name', ]);

            autocomplete.addListener('place_changed', () => {
                var place = autocomplete.getPlace();

                if (!place.geometry) {
                    window.alert(`No details available for input: ${place.name}`);
                    return;
                }

                this.setCenter(place.geometry.location.lat(), place.geometry.location.lng());

                let locality = '';
                let street = [];

                for (let component of place.address_components) {
                    switch (component.types[0]) {
                        case 'street_number':
                        case 'route':
                        case 'sublocality_level_1':
                        case 'sublocality_level_2':
                            street.push(component.long_name);
                            break;
                        case 'locality':
                            locality = component.long_name;
                            break;
                    }
                }

                let streetInput = document.getElementById('manual-address-street-input');
                let suburbInput = document.getElementById('manual-address-suburb-input');
                streetInput.value = street.join(', ');
                streetInput.dispatchEvent(new Event('input'));
                suburbInput.value = locality;
                suburbInput.dispatchEvent(new Event('input'));

                let latInput = document.getElementById('hidden-input-lat');
                let lngInput = document.getElementById('hidden-input-lng');
                latInput.value = place.geometry.location.lat();
                latInput.dispatchEvent(new Event('input'));
                lngInput.value = place.geometry.location.lng();
                lngInput.dispatchEvent(new Event('input'));
            });
        },
        setCenter (lat, lng) {
            this.imgUrl = `/virtual-valuation-maps-url?lat=${lat}&lng=${lng}`;
        },
        validate (element) {
            element.classList.toggle('input-error', !element.validity.valid);
            element.setCustomValidity('');
            return element.validity.valid;
        },
        validatePropertyLocation () {
            this.validate(document.getElementById('pac-input'));
            let streetValid = this.validate(document.getElementById('manual-address-street-input'));
            let suburbValid = this.validate(document.getElementById('manual-address-suburb-input'));

            let pacInput = document.getElementById('pac-input');
            if (!streetValid && !suburbValid && this.mapAddressInput) {
                pacInput.classList.add('input-error');
            }

            return streetValid && suburbValid;
        },
        validatePropertyDetails () {
            let typeSelect = document.getElementById('property-details-type-select');
            return this.validate(typeSelect);
        },
        validateContactDetails () {
            let firstnameInput = document.getElementById('contact-details-firstname-input');
            let lastnameInput = document.getElementById('contact-details-lastname-input');

            let fullnameInput = document.getElementById('hidden-input-fullname');
            fullnameInput.value = firstnameInput.value + ' ' + lastnameInput.value;
            fullnameInput.dispatchEvent(new Event('input'));

            let firstnameValid = this.validate(firstnameInput);
            let lastnameValid = this.validate(lastnameInput);
            let emailValid = this.validate(document.getElementById('contact-details-email-input'));
            let phoneValid = this.validate(document.getElementById('contact-details-phone-input'));

            return firstnameValid
                && lastnameValid
                && emailValid
                && phoneValid
            ;
        },
        formSubmit () {
            if (this.validateContactDetails()) {
                trackGa('Virtual Valuation', 'Submit');

                document.getElementById('valuation-form-element').submit();
            }
        },
    },
};
</script>

<style lang="scss">
.valuation-form-header {
    position: relative;
    background: white;
    max-width: 920px;
    margin: 0 auto;
    padding: 32px 0;
    border-bottom: 1px solid #f3f3f3;
    box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.16);
    text-align: center;
}

.section-title {
    font-size: 14px;
    line-height: 17px;
    color: #000;
    font-weight: 400;
    padding: 0px 51px;
    position: relative;

    &.active::before {
        content: '';
        display: block;
        background: #ffd503;
        height: 16px;
        width: 16px;
        position: absolute;
        left: 0;
        right: 0;
        bottom: -34px;
        border: 2px solid white;
        border-radius: 50%;
        margin: 0 auto;
        z-index: 1;
    }

    @media (max-width: 740px) {
        font-size: 18px;

        &:not(.active) {
            display: none;
        }

        &.active::before {
            display: none;
        }
    }
}

.section-progress-bar {
    background: #f3f3f3;
    height: 10px;
    max-width: 416px;
    margin: 0 auto;
    margin-top: 15px;
    border-radius: 10px;
    position: relative;
    transition: all .5s ease;

    &::after {
        content: '';
        background: #ffd503;
        width: 0;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 10px;
        border-radius: 10px;
        transition: all .5s ease;

        .section-title.active:nth-child(2) ~ & {
            width: 52%;
        }

        .section-title.active:nth-child(3) ~ & {
            width: 100%;
        }
    }

    @media (max-width: 740px) {
        display: none;

        &::after {
            display: none;
        }
    }
}

.valuation-form-section {
    position: relative;
    background: white;
    max-width: 920px;
    margin: 0 auto;
    box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.16);
    min-height: 410px;
}

#pac-input {
    position: absolute;
    top: 29px;
    left: 0;
    right: 0;
    margin: 0 auto;
    max-width: 690px;
    display: block;
    width: calc(100% - 58px);
    height: 45px;
    padding: 0px 20px;
    font-size: 14px;
    line-height: 14px;
    background: white;
    color: black;
    border: none;

    &.input-error {
        border: 3px solid rgb(192, 0, 0);
    }
}

.map-address-input {
    height: 410px;
}

.manual-address-input {
    background: #fff;
    padding: 43px 20px 56px;
    max-width: 333px;
    margin: 0 auto;

    @media (max-width: 740px) {
        padding: 43px 20px 56px;
        max-width: 100%;
    }
}

.property-details-input {
    padding: 43px 193px 56px;

    @media (max-width: 740px) {
        padding: 23px 20px 56px;
    }
}

.contact-details-input {
    display: flex;
    flex-wrap: wrap;
    padding: 50px 20px 56px;
}

#google-map {
    background-position: center;
    background-size: cover;
    height: 100%;
}

.form-input-label-element {
    display: block;
    margin-bottom: 21px;
}

.form-input-label {
    margin-bottom: 10px;
    font-size: 14px;
    display: inline-block;
}

.form-input-error {
    display: none;
    font-size: 14px;
    color: red;

    input.input-error + &, select.input-error + & {
        display: initial;
    }
}

.form-input-gridbox {
    width: 48%;
    margin-right: 4%;

    &:nth-of-type(2n) {
        margin-right: 0;
    }

    @media (max-width: 740px) {
        width: 100%;
        margin-right: 0;
    }
}

.valuation-form-input-box {
    font-weight: 300;
    height: 34px;
    padding-left: 11px;
    width: 100%;
    font-size: 18px;
    margin-bottom: 2px;
    background: #f3f3f3;
    color: black;
    border: none;

    &.input-error {
        border: 3px solid rgb(192, 0, 0);
    }
}

.valuation-form-select-box {
    appearance: none;
    font-weight: 300;
    height: 40px;
    width: 100%;
    font-size: 14px;
    background: white;
    color: black;
    border: 1px solid #b4b4b4;
    padding: 7px 12px;


    &.input-error {
        border: 3px solid rgb(192, 0, 0);
        padding: 5px 10px;
    }

    &-arrow {
        pointer-events: none;
        width: 100%;
        position: relative;
        &::after {
            content: "▼";
            top: 6px;
            right: 10px;
            position: absolute;
        }
    }
}

.valuation-form-checkbox {
    vertical-align: text-bottom;
    margin-right: 4px;
}

.valuation-form-section-footer {
    position: absolute;
    width: 100%;
    bottom: 0;
    > .valuation-form-section-footer--prev {
        position: absolute;
        bottom: 28px;
        left: 26px;
        cursor: pointer;
        padding: 8px 23px;
        font-size: 16px;
        transition: all .5s ease;
        box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.16);
        color: black;
        background: white;
        border: 2px solid transparent;

        &:hover {
            border: 2px solid black;
        }
    }

    > .valuation-form-section-footer--next {
        position: absolute;
        bottom: 28px;
        right: 26px;
        cursor: pointer;
        padding: 8px 23px;
        font-size: 16px;
        text-transform: uppercase;
        transition: all .5s ease;
        box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.16);
        color: white;
        background: black;
        border: 2px solid transparent;

        &:hover {
            color: black;
            background: white;
            border: 2px solid black;
        }
    }

    > .valuation-form-section-footer--submit {
        position: absolute;
        bottom: 28px;
        right: 26px;
        cursor: pointer;
        color: #000;
        font-size: 16px;
        text-transform: uppercase;
        transition: all .5s ease;
        background: #ffd504;
        padding: 8px 22px;
        box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.16);
        border: 2px solid #ffd504;

        &:hover {
            border: 2px solid black;
        }
    }

    > .valuation-form-section-footer--map-manual {
        position: absolute;
        bottom: 28px;
        left: 0;
        right: 0;
        cursor: pointer;
        font-size: 14px;
        margin: 0 auto;
        padding: 5px 15px;
        background: white;
        border: none;

        @media (max-width: 740px) {
            margin: initial;
            right: initial;

            left: 30px;
        }

        @media (max-width: 430px) {
            left: 14px;
            padding: 5px 8px;
        }

        &:hover {
            text-decoration: underline 1px;
        }

        &:active {
            color: black;
        }
    }
}
</style>
